import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref } from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";

import { PageBackground } from "./PageBackground";

export type EntityFormPageAsideContext = {
    isNarrow: boolean;
    stickyTop: number;
};

export type EntityFormPageProps = {
    header: (ref: Ref<HTMLElement>) => ReactNode;
    children: ReactNode;
    aside?: ReactNode | ((context: EntityFormPageAsideContext) => ReactNode);
};

export type EntityFormPageStateProps = {
    children: ReactNode;
};

const entityFormHeaderHeightVariable = "--entity-form-header-height";

/** Sticky descendants stay below the measured shared form header. */
export const getEntityFormStickyTop = (offset: string) =>
    `calc(var(${entityFormHeaderHeightVariable}, 0px) + ${offset})`;

/** Shared responsive canvas for loading, empty and error states rendered outside the full form shell. */
export const EntityFormPageState = ({ children }: EntityFormPageStateProps) => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));

    return (
        <PageBackground>
            <Box
                data-testid="entity-form-page-state"
                sx={{
                    paddingBlock: theme.spacing(3),
                    paddingInline: isNarrow ? theme.spacing(2) : theme.spacing(6),
                }}
            >
                {children}
            </Box>
        </PageBackground>
    );
};

/** Shared entity form canvas, measured header and responsive main/aside composition. */
export const EntityFormPage = ({ header, children, aside }: EntityFormPageProps) => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));
    const headerRef = useRef<HTMLElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useLayoutEffect(() => {
        const element = headerRef.current;
        if (!element) return;

        const update = () => setHeaderHeight(element.getBoundingClientRect().height);
        update();
        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const renderedAside = typeof aside === "function" ? aside({ isNarrow, stickyTop: headerHeight }) : aside;

    return (
        <PageBackground>
            {header(headerRef)}
            <Stack
                data-testid="entity-form-page-content"
                direction={isNarrow ? "column" : "row"}
                alignItems="flex-start"
                style={{ [entityFormHeaderHeightVariable]: `${headerHeight}px` } as CSSProperties}
                sx={{
                    gap: theme.spacing(3),
                    paddingInline: isNarrow ? theme.spacing(2) : theme.spacing(6),
                    paddingBottom: theme.spacing(10),
                }}
            >
                <Stack
                    data-testid="entity-form-page-main"
                    flex={isNarrow ? undefined : 1}
                    minWidth={0}
                    sx={{ gap: theme.spacing(2), width: isNarrow ? "100%" : undefined }}
                >
                    {children}
                </Stack>
                {renderedAside}
            </Stack>
        </PageBackground>
    );
};
